import { useLayoutEffect, useRef, useState, type ReactNode } from "react"
import { useWindowVirtualizer } from "@tanstack/react-virtual"

import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "cn"

const SKELETON_ROW_COUNT = 8

export type TableColumn<T> = {
  id: string
  header: ReactNode
  cell: (row: T, index: number) => ReactNode
  sticky?: boolean
  className?: string
  skeleton?: ReactNode
}

export type TableShellProps<T> = {
  columns: TableColumn<T>[]
  rows: T[]
  getRowId: (row: T, index: number) => string
  empty?: ReactNode
  className?: string
  /** Window-virtualize the body so large browse sets do not mount every row. */
  virtualize?: boolean
  isLoading?: boolean
}

/**
 * Presentational table for Opportunities compound cells. Maps a column list
 * onto shadcn Table. Sticky actions use CSS `sticky right-0` — no TanStack
 * column sort/filter.
 */
export function TableShell<T>({
  columns,
  rows,
  getRowId,
  empty,
  className,
  virtualize,
  isLoading,
}: TableShellProps<T>) {
  if (isLoading) {
    return (
      <Table className={className}>
        <TableHeadRow columns={columns} />
        <TableBody>
          {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
            <SkeletonTableRow key={index} columns={columns} />
          ))}
        </TableBody>
      </Table>
    )
  }

  if (virtualize && rows.length > 0) {
    return (
      <VirtualizedTableShell
        columns={columns}
        rows={rows}
        getRowId={getRowId}
        className={className}
      />
    )
  }

  return (
    <Table className={className}>
      <TableHeadRow columns={columns} />
      <TableBody>
        {rows.length === 0 ? (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={columns.length} className="whitespace-normal">
              {empty ?? null}
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row, index) => (
            <DataTableRow
              key={getRowId(row, index)}
              columns={columns}
              row={row}
              index={index}
            />
          ))
        )}
      </TableBody>
    </Table>
  )
}

function TableHeadRow<T>({ columns }: { columns: TableColumn<T>[] }) {
  return (
    <TableHeader>
      <TableRow>
        {columns.map((column) => (
          <TableHead
            key={column.id}
            className={cn(
              column.sticky && "sticky right-0 z-10 bg-background",
              column.className
            )}
          >
            {column.header}
          </TableHead>
        ))}
      </TableRow>
    </TableHeader>
  )
}

function SkeletonTableRow<T>({ columns }: { columns: TableColumn<T>[] }) {
  return (
    <TableRow>
      {columns.map((column) => (
        <TableCell
          key={column.id}
          className={cn(
            column.sticky && "sticky right-0 z-10 bg-background",
            column.className
          )}
        >
          {column.skeleton ?? <Skeleton className="h-4 w-full" />}
        </TableCell>
      ))}
    </TableRow>
  )
}

function DataTableRow<T>({
  columns,
  row,
  index,
  rowRef,
  dataIndex,
}: {
  columns: TableColumn<T>[]
  row: T
  index: number
  rowRef?: (node: HTMLTableRowElement | null) => void
  dataIndex?: number
}) {
  return (
    <TableRow ref={rowRef} data-index={dataIndex}>
      {columns.map((column) => (
        <TableCell
          key={column.id}
          className={cn(
            column.sticky && "sticky right-0 z-10 bg-background",
            column.className
          )}
        >
          {column.cell(row, index)}
        </TableCell>
      ))}
    </TableRow>
  )
}

function VirtualizedTableShell<T>({
  columns,
  rows,
  getRowId,
  className,
}: Omit<TableShellProps<T>, "empty" | "virtualize">) {
  const listRef = useRef<HTMLDivElement>(null)
  const [scrollMargin, setScrollMargin] = useState(0)

  useLayoutEffect(() => {
    const node = listRef.current
    if (!node) {
      return
    }
    setScrollMargin(node.getBoundingClientRect().top + window.scrollY)
  }, [rows.length])

  const virtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: () => 80,
    overscan: 12,
    scrollMargin,
    getItemKey: (index) => getRowId(rows[index], index),
  })

  const virtualRows = virtualizer.getVirtualItems()
  const paddingTop =
    virtualRows.length > 0 ? Math.max(0, virtualRows[0].start - scrollMargin) : 0
  const paddingBottom =
    virtualRows.length > 0
      ? Math.max(0, virtualizer.getTotalSize() - (virtualRows[virtualRows.length - 1].end - scrollMargin))
      : 0

  return (
    // Offset for useWindowVirtualizer; not a layout wrapper.
    <div ref={listRef}>
      <Table className={className}>
        <TableHeadRow columns={columns} />
        <TableBody>
          {paddingTop > 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell
                colSpan={columns.length}
                className="p-0"
                style={{ height: paddingTop }}
              />
            </TableRow>
          ) : null}
          {virtualRows.map((virtualRow) => {
            const row = rows[virtualRow.index]
            return (
              <DataTableRow
                key={getRowId(row, virtualRow.index)}
                columns={columns}
                row={row}
                index={virtualRow.index}
                dataIndex={virtualRow.index}
                rowRef={virtualizer.measureElement}
              />
            )
          })}
          {paddingBottom > 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell
                colSpan={columns.length}
                className="p-0"
                style={{ height: paddingBottom }}
              />
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </div>
  )
}
